import { useCallback, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE = (() => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1"

    return isLocalHost ? (["http://localhost", "3000/api"].join(":")) : "/api"
  }

  return "/api"
})()

function normalizeOdontogramPayload(payload) {
  if (!payload) return null

  const history = Array.isArray(payload?.history) ? payload.history : []
  const latestEntries = Array.isArray(payload?.latestEntries) ? payload.latestEntries : []
  const allEntries = Array.isArray(payload?.allEntries)
    ? payload.allEntries
    : history.length
      ? history
      : latestEntries

  return {
    ...payload,
    history,
    latestEntries,
    allEntries,
    entries: allEntries
  }
}

function buildPatientFinanceParams(targetPatientId, targetClinicId) {
  const params = new URLSearchParams()

  if (targetPatientId) {
    params.set("patientId", String(targetPatientId))
  }

  if (targetClinicId && targetClinicId !== "ALL") {
    params.set("clinicId", String(targetClinicId))
  }

  return params
}

export default function usePatientWorkspace({
  patientId,
  clinicId
}) {
  const [loadingWorkspace, setLoadingWorkspace] = useState(false)

  const [patientSummary, setPatientSummary] = useState(null)
  const [patientAppointments, setPatientAppointments] = useState([])
  const [patientPlans, setPatientPlans] = useState([])
  const [patientOdontogram, setPatientOdontogram] = useState(null)

  const [patientFinance, setPatientFinance] = useState(null)
  const [loadingFinance, setLoadingFinance] = useState(false)

  const [openCredits, setOpenCredits] = useState([])
  const [pendingReceivables, setPendingReceivables] = useState([])
  const [loadingAdvancedFinance, setLoadingAdvancedFinance] = useState(false)

  const [patientAllocations, setPatientAllocations] = useState([])
  const [financialTimeline, setFinancialTimeline] = useState([])
  const [loadingFinancialTimeline, setLoadingFinancialTimeline] = useState(false)

  const [patientTimeline, setPatientTimeline] = useState([])
  const [loadingPatientTimeline, setLoadingPatientTimeline] = useState(false)

  const [patientMergeAudits, setPatientMergeAudits] = useState([])
  const [loadingPatientMergeAudits, setLoadingPatientMergeAudits] = useState(false)

  const resetFinanceWorkspace = useCallback(() => {
    setPatientFinance(null)
    setOpenCredits([])
    setPendingReceivables([])
    setPatientAllocations([])
    setFinancialTimeline([])
  }, [])

  const clearWorkspace = useCallback(() => {
    setPatientSummary(null)
    setPatientAppointments([])
    setPatientPlans([])
    setPatientOdontogram(null)
    setPatientTimeline([])
    setPatientMergeAudits([])
    setFinancialTimeline([])
    resetFinanceWorkspace()
  }, [resetFinanceWorkspace])

  const loadFinancialTimeline = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId) {
        setFinancialTimeline([])
        return
      }

      setLoadingFinancialTimeline(true)

      const params = buildPatientFinanceParams(targetPatientId, targetClinicId)

      const res = await apiFetch(
        `${API_BASE}/finance/patient-financial-timeline?${params.toString()}`
      )
      const data = await res.json().catch(() => [])

      setFinancialTimeline(res.ok && Array.isArray(data) ? data : [])
    } catch {
      setFinancialTimeline([])
    } finally {
      setLoadingFinancialTimeline(false)
    }
  }, [patientId, clinicId])

  const loadPatientFinance = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId) {
        setPatientFinance(null)
        return
      }

      setLoadingFinance(true)

      const params = buildPatientFinanceParams(targetPatientId, targetClinicId)

      const res = await apiFetch(
        `${API_BASE}/finance/patient-finance-summary?${params.toString()}`
      )
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setPatientFinance(null)
        return
      }

      setPatientFinance(data || null)
    } catch {
      setPatientFinance(null)
    } finally {
      setLoadingFinance(false)
    }
  }, [patientId, clinicId])

  const loadAdvancedFinance = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId) {
        setOpenCredits([])
        setPendingReceivables([])
        return
      }

      setLoadingAdvancedFinance(true)

      const params = buildPatientFinanceParams(targetPatientId, targetClinicId)
      const query = params.toString()

      const [creditsRes, receivablesRes] = await Promise.all([
        apiFetch(`${API_BASE}/finance/patient-open-credits?${query}`),
        apiFetch(`${API_BASE}/finance/patient-pending-receivables?${query}`)
      ])

      const creditsData = await creditsRes.json().catch(() => [])
      const receivablesData = await receivablesRes.json().catch(() => [])

      setOpenCredits(creditsRes.ok && Array.isArray(creditsData) ? creditsData : [])
      setPendingReceivables(
        receivablesRes.ok && Array.isArray(receivablesData) ? receivablesData : []
      )
    } catch {
      setOpenCredits([])
      setPendingReceivables([])
    } finally {
      setLoadingAdvancedFinance(false)
    }
  }, [patientId, clinicId])

  const loadPatientAllocations = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId) {
        setPatientAllocations([])
        return
      }

      const params = buildPatientFinanceParams(targetPatientId, targetClinicId)

      const res = await apiFetch(
        `${API_BASE}/finance/patient-allocations?${params.toString()}`
      )
      const data = await res.json().catch(() => [])

      setPatientAllocations(res.ok && Array.isArray(data) ? data : [])
    } catch {
      setPatientAllocations([])
    }
  }, [patientId, clinicId])

  const loadPatientMergeAudits = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId || !targetClinicId || targetClinicId === "ALL") {
        setPatientMergeAudits([])
        setLoadingPatientMergeAudits(false)
        return
      }

      setLoadingPatientMergeAudits(true)

      const params = new URLSearchParams({
        clinicId: String(targetClinicId),
        _ts: String(Date.now())
      })

      const res = await apiFetch(
        `${API_BASE}/patients/${targetPatientId}/merge-audits?${params.toString()}`
      )
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setPatientMergeAudits([])
        return
      }

      setPatientMergeAudits(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setPatientMergeAudits([])
    } finally {
      setLoadingPatientMergeAudits(false)
    }
  }, [patientId, clinicId])

  const loadPatientTimeline = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId || !targetClinicId || targetClinicId === "ALL") {
        setPatientTimeline([])
        return
      }

      setLoadingPatientTimeline(true)

      const params = new URLSearchParams({
        patientId: String(targetPatientId),
        clinicId: String(targetClinicId)
      })

      const res = await apiFetch(
        `${API_BASE}/appointments/patient-timeline?${params.toString()}`
      )
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setPatientTimeline([])
        return
      }

      setPatientTimeline(Array.isArray(data?.items) ? data.items : [])
    } catch {
      setPatientTimeline([])
    } finally {
      setLoadingPatientTimeline(false)
    }
  }, [patientId, clinicId])

  const loadPatientWorkspace = useCallback(async (
    targetPatientId = patientId,
    targetClinicId = clinicId
  ) => {
    try {
      if (!targetPatientId || !targetClinicId || targetClinicId === "ALL") {
        clearWorkspace()
        return { ok: false, reason: "MISSING_CONTEXT" }
      }

      setLoadingWorkspace(true)

      const params = new URLSearchParams({
        patientId: String(targetPatientId),
        clinicId: String(targetClinicId)
      })

      const [summaryRes, appointmentsRes, plansRes, odontoRes] = await Promise.all([
        apiFetch(`${API_BASE}/appointments/patient-clinical-snapshot?${params.toString()}`),
        apiFetch(`${API_BASE}/appointments/patient-appointments?${params.toString()}`),
        apiFetch(`${API_BASE}/appointments/patient-treatment-plans?${params.toString()}`),
        apiFetch(`${API_BASE}/appointments/patient-odontogram-workspace?${params.toString()}`)
      ])

      if (summaryRes.status === 400) {
        clearWorkspace()
        return { ok: false, reason: "PATIENT_NOT_IN_CLINIC" }
      }

      const summaryData = await summaryRes.json().catch(() => null)
      const appointmentsData = await appointmentsRes.json().catch(() => [])
      const plansData = await plansRes.json().catch(() => [])
      const odontoData = await odontoRes.json().catch(() => null)

      setPatientSummary(summaryRes.ok ? summaryData : null)
      setPatientAppointments(
        appointmentsRes.ok && Array.isArray(appointmentsData) ? appointmentsData : []
      )
      setPatientPlans(plansRes.ok && Array.isArray(plansData) ? plansData : [])
      setPatientOdontogram(odontoRes.ok ? normalizeOdontogramPayload(odontoData) : null)

      await Promise.all([
        loadPatientFinance(targetPatientId, targetClinicId),
        loadAdvancedFinance(targetPatientId, targetClinicId),
        loadPatientAllocations(targetPatientId, targetClinicId),
        loadFinancialTimeline(targetPatientId, targetClinicId),
        loadPatientTimeline(targetPatientId, targetClinicId),
        loadPatientMergeAudits(targetPatientId, targetClinicId)
      ])

      return { ok: true }
    } catch {
      clearWorkspace()
      return { ok: false, reason: "UNKNOWN_ERROR" }
    } finally {
      setLoadingWorkspace(false)
    }
  }, [
    patientId,
    clinicId,
    clearWorkspace,
    loadPatientFinance,
    loadAdvancedFinance,
    loadPatientAllocations,
    loadFinancialTimeline,
    loadPatientTimeline,
    loadPatientMergeAudits
  ])

  return {
    loadingWorkspace,

    patientSummary,
    patientAppointments,
    patientPlans,
    patientOdontogram,

    patientFinance,
    loadingFinance,

    openCredits,
    pendingReceivables,
    loadingAdvancedFinance,

    patientAllocations,
    financialTimeline,
    loadingFinancialTimeline,

    patientTimeline,
    loadingPatientTimeline,

    patientMergeAudits,
    loadingPatientMergeAudits,

    resetFinanceWorkspace,
    clearWorkspace,
    loadPatientWorkspace,
    loadPatientFinance,
    loadAdvancedFinance,
    loadPatientAllocations,
    loadFinancialTimeline,
    loadPatientTimeline
  }
}
